import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useId, useMemo, useState } from 'react'
import type { UrlObject } from 'url'
import { Button } from '@/components/Button'
import { useSavePathBookmark } from '@/helpers/usePathBookmarks'
import { cn } from '@/utils/cn'
import { printLocalAmount } from '@/utils/misc'
import {
  MAX_CAR_PAYOUT,
  MIN_CAR_PAYOUT,
  buildStepRanges,
  loanAmountToSliderValue,
  sliderValueToLoanAmount,
  snapLoanAmount,
  totalSteps,
} from './loanAmountSlider'

export { MAX_CAR_PAYOUT, MIN_CAR_PAYOUT }

export interface LoanAmountCardProps {
  className?: string
  ctaText: string
  /** Label above the amount; defaults to "choose loan amount". */
  label?: string
  /** Preselected amount (clamped to the slider range); defaults to 5.000 €. */
  initialAmount?: number
  /** Where the CTA goes; defaults to the car pawn form's first step. */
  buildHref?: (loanAmount: number) => UrlObject
  /** Handle the CTA in place instead of navigating (e.g. send a request). */
  onSubmit?: (loanAmount: number) => void
  isSubmitting?: boolean
  /** Upper end of the slider (default 200.000 €; motorcycles use 75.000 €). */
  maxAmount?: number
  /** Lower end of the slider (default 500 €; handbags use 250 €). */
  minAmount?: number
}

const DEFAULT_LOAN_AMOUNT = 5000

export default function LoanAmountCard(props: LoanAmountCardProps) {
  const { t } = useTranslation()

  const inputId = useId()
  const savePathBookmark = useSavePathBookmark()
  const ranges = useMemo(
    () => buildStepRanges(props.maxAmount, props.minAmount),
    [props.maxAmount, props.minAmount],
  )
  const steps = totalSteps(ranges)
  const [loanAmount, _setLoanAmount] = useState(() =>
    snapLoanAmount(props.initialAmount ?? DEFAULT_LOAN_AMOUNT, ranges),
  )
  const [inputValue, setInputValue] = useState(() =>
    printLocalAmount({
      number: loanAmount,
      fractionDigits: 0,
      removeSpaces: true,
    }),
  )

  const setLoanAmount = (amount: number) => {
    _setLoanAmount(amount)
    setInputValue(
      printLocalAmount({
        number: amount,
        fractionDigits: 0,
        removeSpaces: true,
      }),
    )
  }

  const sliderValue = loanAmountToSliderValue(loanAmount, ranges)

  return (
    <form
      className={cn(
        'p-3 rounded-xl flex flex-col items-center shadow-xl gap-y-2 max-w-80 bg-white',
        props.className,
      )}
      onSubmit={(event) => event.preventDefault()}
    >
      <label
        className="text-primary font-semibold leading-[140%]"
        htmlFor={inputId}
      >
        {props.label ?? t('common:choose_loan_amount')}
      </label>

      {/** Safari < 17 does not support line-height in inputs */}
      <div className="border border-neutral-900 rounded-xl h-14 flex justify-center items-center overflow-hidden">
        <input
          className="w-full tabular-nums text-brand-600 text-5xl font-bold text-center outline-none"
          id={inputId}
          inputMode="numeric"
          name="loan-amount"
          value={inputValue}
          onFocus={(event) => {
            event.target.setSelectionRange(0, event.target.value.length - 1)
          }}
          onKeyDown={(event) => {
            const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight']
            if (
              event.ctrlKey ||
              event.metaKey ||
              allowedKeys.includes(event.key)
            ) {
              return
            }

            if (
              event.currentTarget.value.endsWith('€') &&
              event.currentTarget.value.length ===
                event.currentTarget.selectionEnd
            ) {
              if (
                event.currentTarget.selectionStart ??
                0 >= event.currentTarget.selectionEnd - 1
              ) {
                event.preventDefault()
              } else {
                event.currentTarget.selectionEnd--
              }
            }

            if (!event.key.match(amountInputAllowedRegEx)) {
              event.preventDefault()
            }
          }}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text')

            if (!text.match(amountInputAllowedRegEx)) {
              event.preventDefault()
            }
          }}
          onChange={(event) => {
            setInputValue(event.target.value)
          }}
          onBlur={(event) => {
            const value = Number.parseFloat(
              event.target.value.replaceAll('.', '').replaceAll(',', '.'),
            )

            if (Number.isNaN(value)) {
              return
            }

            setLoanAmount(snapLoanAmount(value, ranges))
          }}
        />
      </div>

      <input
        type="range"
        name="loan-amount-step"
        className="w-full car-hero-slider"
        aria-hidden
        tabIndex={-1}
        min={0}
        max={steps}
        value={sliderValue}
        style={
          {
            '--slider-value-percent': `${((sliderValue / steps) * 100).toFixed(2)}%`,
          } as React.CSSProperties
        }
        onChange={(event) => {
          setLoanAmount(
            sliderValueToLoanAmount(event.target.valueAsNumber, ranges),
          )
        }}
      />

      {props.onSubmit ? (
        <Button
          type="button"
          variant="cta"
          className="w-full"
          disabled={props.isSubmitting}
          loading={props.isSubmitting}
          onClick={() => props.onSubmit?.(loanAmount)}
        >
          {props.ctaText}
        </Button>
      ) : (
        <Button variant="cta" asChild className="w-full">
          <Link
            onClick={savePathBookmark}
            href={
              props.buildHref?.(loanAmount) ?? {
                pathname: '/autopfand-formular',
                query: { betrag: loanAmount },
              }
            }
          >
            {props.ctaText}
          </Link>
        </Button>
      )}
    </form>
  )
}

const amountInputAllowedRegEx = /^[\d.]+$/
